/**
 * Sites — dashboard-facing list + management CRUD.
 *
 * Two surfaces:
 *   - `/v1/sites` (dashboard list) — org-scoped, JWT. Any member.
 *     Used by `useOrigins()` (alias kept for continuity with
 *     topology.md which names the top-level route "Origins").
 *   - `/v1/management/sites/{uuid}` + `/v1/management/teams/…/sites`
 *     — full CRUD. `org_role=admin` required for writes.
 *
 * audit-config-on-sites collapsed the `monitors` + `site_settings`
 * tables into per-site `audit.*` columns. Two endpoints replace the
 * old monitor surface:
 *
 *   - `PATCH /v1/management/sites/{uuid}` with `body.audit:{}` —
 *     replaces the old `PUT /settings` and per-monitor mutations.
 *     Surfaced as `useUpdateSiteAuditConfig(siteUuid)`.
 *   - `POST /v1/management/sites/{uuid}/audits/run` — fires the
 *     full grid (`regions × devices × enabled pages`); returns a
 *     `snapshot_group_uuid` plus a snippet of the dispatched audits.
 *   - `POST /v1/management/sites/{uuid}/audits/run-combination` —
 *     a single `{page, device, region}` tuple; 400
 *     INVALID_COMBINATION if the tuple isn't in the current grid.
 *
 * The legacy `PUT /v1/management/sites/{uuid}` still exists for
 * back-compat (`monitoring_enabled` toggle); the PATCH endpoint is
 * the canonical write for everything audit-config sits on.
 */
import type { QueryClient } from '@tanstack/react-query';
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from '../api';
import type {
  LatestMetricRow,
  ListSitesResponse,
  P75Row,
  Page,
  PageWithRecentAudits,
  PatchAuditConfig,
  RunAuditsResponse,
  Site,
  SiteOverview,
  TrendBucketRow,
} from '../api-types';
import { auditKeys, pageKeys, siteKeys } from './query-keys';

export interface SitesFilter {
  limit?: number;
  offset?: number;
}

function qs(filter: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/**
 * Normalise a `SitesFilter` so equivalent shapes produce the same
 * cache key + URL. `offset: 0` is the API default — callers that pass
 * it explicitly and callers that omit it used to produce two
 * different cache entries → two HTTP requests for the exact same
 * data. Strip it here so the React Query cache coalesces them.
 */
function normaliseSitesFilter(filter: SitesFilter): SitesFilter {
  const normalised: SitesFilter = {};
  if (filter.limit !== undefined) normalised.limit = filter.limit;
  if (filter.offset !== undefined && filter.offset !== 0) normalised.offset = filter.offset;
  return normalised;
}

/**
 * Origin/site list for the whole active org. Backs the Origins page,
 * the sidebar "recently viewed origins" section, and — aggregated —
 * the scheduled-sites widget on Home.
 */
export function useOrigins(
  filter: SitesFilter = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<ListSitesResponse> {
  const normalised = normaliseSitesFilter(filter);
  return useQuery({
    queryKey: siteKeys.list(normalised),
    queryFn: () => apiFetch<ListSitesResponse>(`/v1/sites${qs({ ...normalised })}`),
    enabled: options.enabled ?? true,
  });
}

/** Alias — matches file name in the phase doc. */
export const useSites = useOrigins;

/**
 * Single-site detail. Hydrates synchronously from the
 * `useOrigins({limit:200})` cache when the row is already there —
 * which it almost always is on dashboard navigations, because the
 * sites list is part of the app-shell bootstrap.
 *
 * The fetch is gated on `origins.isFetched && !cached` so the
 * dedicated `/v1/management/sites/:uuid` GET only fires when (a)
 * we genuinely don't have the row in the list (cross-org nav, page
 * deep-link before the list loaded) — or (b) the list failed and
 * we need to try the dedicated endpoint as a fallback. On the hot
 * path (typical navigation between sites in the same org) the row
 * is already in origins and we never hit the network for it again.
 *
 * `initialData` + a generous `staleTime` keep the cached value
 * authoritative for the session. Mutations on the site (audit-
 * config save, monitoring toggle, delete) already invalidate
 * `siteKeys.detail(uuid)` to force a refresh when fresh data is
 * actually needed.
 */
export function useSite(uuid: string | null | undefined): UseQueryResult<Site> {
  const origins = useOrigins({ limit: 200 });
  const cached = origins.data?.sites.find((s) => s.uuid === uuid);
  return useQuery({
    queryKey: siteKeys.detail(uuid ?? ''),
    queryFn: () => apiFetch<Site>(`/v1/management/sites/${uuid}`),
    // `origins.isFetched` flips true once the list query has either
    // resolved or errored. Waiting for it on cold-load means the
    // useSite fetch doesn't race the useOrigins fetch — when both
    // return the same site, that's one wasted call. After origins
    // lands, if the site is in the list we skip the dedicated GET
    // entirely (cached path); otherwise we fall through.
    enabled: !!uuid && origins.isFetched && !cached,
    initialData: cached,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSiteLatestMetrics(
  uuid: string | null | undefined,
  filter: { page_uuid?: string; device_profile_label?: string } = {},
): UseQueryResult<LatestMetricRow[]> {
  return useQuery({
    queryKey: siteKeys.latest(uuid ?? '', filter),
    queryFn: () => apiFetch<LatestMetricRow[]>(`/v1/sites/${uuid}/metrics/latest${qs(filter)}`),
    enabled: !!uuid,
  });
}

export function useSiteP75(
  uuid: string | null | undefined,
  filter: {
    days?: number;
    device_type?: string;
    device_profile_label?: string;
    region?: string;
  } = {},
): UseQueryResult<P75Row[]> {
  return useQuery({
    queryKey: siteKeys.p75(uuid ?? '', filter),
    queryFn: () => apiFetch<P75Row[]>(`/v1/sites/${uuid}/metrics/p75${qs(filter)}`),
    enabled: !!uuid,
  });
}

export function useSiteTrend(
  uuid: string | null | undefined,
  filter: {
    interval?: 'hour' | 'day';
    range?: number;
    page_uuid?: string;
    device_type?: string;
    device_profile_label?: string;
    region?: string;
  } = {},
): UseQueryResult<TrendBucketRow[]> {
  return useQuery({
    queryKey: siteKeys.trend(uuid ?? '', filter),
    queryFn: () => apiFetch<TrendBucketRow[]>(`/v1/sites/${uuid}/metrics/trend${qs(filter)}`),
    enabled: !!uuid,
  });
}

/** Pages belonging to a site. */
export function useSitePages(
  uuid: string | null | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<Page[]> {
  return useQuery({
    queryKey: siteKeys.pages(uuid ?? ''),
    queryFn: () => apiFetch<Page[]>(`/v1/management/sites/${uuid}/pages`),
    enabled: !!uuid && (options.enabled ?? true),
  });
}

/**
 * Pages + the last N audits per page for the requested metrics. Backs
 * the sparkline column on the site-overview Pages list.
 *
 * The metric set is part of the cache key so flipping a user's
 * preference (via the Customize popover) repopulates the cache cleanly
 * instead of mixing rows shaped for the old prefs with rows shaped for
 * the new. Empty `metrics` returns plain pages — no sparkline data —
 * so the hook is safe to call before workspace prefs have loaded.
 */
export function useSitePagesWithAudits(
  uuid: string | null | undefined,
  metrics: readonly string[],
  options: { enabled?: boolean } = {},
): UseQueryResult<PageWithRecentAudits[]> {
  const csv = metrics.join(',');
  return useQuery({
    queryKey: siteKeys.pagesWithAudits(uuid ?? '', csv),
    queryFn: () =>
      apiFetch<PageWithRecentAudits[]>(
        `/v1/management/sites/${uuid}/pages?include=recent_audits&metrics=${encodeURIComponent(csv)}`,
      ),
    enabled: !!uuid && metrics.length > 0 && (options.enabled ?? true),
  });
}

// ─── Mutations ───────────────────────────────────────────────────

export interface CreateSiteVariables {
  teamUuid: string;
  body: {
    slug: string;
    name: string;
    url: string;
    is_public?: boolean;
    monitoring_enabled?: boolean;
  };
}

export function useCreateSite(): UseMutationResult<Site, Error, CreateSiteVariables> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamUuid, body }) =>
      apiFetch<Site>(`/v1/management/teams/${teamUuid}/sites`, { method: 'POST', body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: siteKeys.lists() });
    },
  });
}

export interface UpdateSiteVariables {
  uuid: string;
  body: Partial<{
    slug: string;
    name: string;
    url: string;
    is_public: boolean;
    monitoring_enabled: boolean;
  }>;
}

/**
 * Edit a site's top-level fields (name, slug, url, visibility,
 * monitoring_enabled toggle) via the PATCH endpoint.
 *
 * For audit configuration (regions, devices, schedule, notifications)
 * use `useUpdateSiteAuditConfig` instead — same PATCH endpoint, but
 * the body is shaped as `{ audit: {...} }` so the merge stays scoped
 * to the audit subtree.
 */
export function useUpdateSite(): UseMutationResult<Site, Error, UpdateSiteVariables> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, body }) =>
      apiFetch<Site>(`/v1/management/sites/${uuid}`, { method: 'PATCH', body }),
    onSuccess: async (_data, vars) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: siteKeys.detail(vars.uuid) }),
        qc.invalidateQueries({ queryKey: siteKeys.lists() }),
      ]);
    },
  });
}

/**
 * Snapshot of sites-list cache entries patched during optimistic
 * delete. Stored as `[queryKey, prevData]` tuples so `onError` can
 * restore the pre-mutation state if the server rejects the delete.
 */
type SitesListSnapshots = Array<readonly [readonly unknown[], unknown]>;

export function useDeleteSite(): UseMutationResult<
  void,
  Error,
  { uuid: string },
  SitesListSnapshots
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid }) => apiFetch<void>(`/v1/management/sites/${uuid}`, { method: 'DELETE' }),
    // ─── Optimistic delete ───────────────────────────────────────────
    //
    // The site row disappears from the sidebar switcher + sites list
    // immediately so the UI feels instant. Server-side this is a
    // *soft* delete with a grace period — the retention sweeper
    // hard-deletes after 7 days. Within that window the user (or
    // support) can restore the row by clearing its `deleted_at`;
    // their Tinybird audit history is intact across all pages on the
    // site.
    onMutate: async ({ uuid }) => {
      // Cancel any in-flight sites-list refetches so they don't race
      // our cache patch and re-introduce the deleted row.
      await qc.cancelQueries({ queryKey: siteKeys.lists() });

      // Snapshot + patch every cached sites-list shape. The data is
      // `ListSitesResponse` ({sites, limit, offset, has_more}); we
      // filter the `sites` array and leave the other fields alone.
      const snapshots: SitesListSnapshots = [];
      const cache = qc.getQueryCache();
      for (const query of cache.findAll({
        predicate: (q) => {
          const k = q.queryKey;
          return Array.isArray(k) && k[0] === 'sites' && k[1] === 'list';
        },
      })) {
        const prev = query.state.data as ListSitesResponse | undefined;
        if (!prev || !Array.isArray(prev.sites)) continue;
        snapshots.push([query.queryKey, prev]);
        qc.setQueryData(query.queryKey, {
          ...prev,
          sites: prev.sites.filter((s) => s.uuid !== uuid),
        } satisfies ListSitesResponse);
      }
      return snapshots;
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, prev] of context) {
        qc.setQueryData(key, prev);
      }
    },
    onSuccess: async (_data, vars) => {
      qc.removeQueries({ queryKey: siteKeys.detail(vars.uuid) });
      await Promise.all([
        qc.invalidateQueries({ queryKey: siteKeys.lists() }),
        qc.invalidateQueries({ queryKey: auditKeys.lists() }),
        qc.invalidateQueries({ queryKey: pageKeys.all }),
      ]);
    },
  });
}

// ─── Audit configuration ─────────────────────────────────────────

export interface UpdateSiteAuditConfigVariables {
  uuid: string;
  body: PatchAuditConfig;
}

/**
 * `PATCH /v1/management/sites/{uuid}` with an `audit:{}` body —
 * the canonical write for audit-config edits. Pass any subset of the
 * `PatchAuditConfig` fields; the server merges with the current row.
 *
 * Returns the full updated Site (with the merged `audit:{}` block) so
 * react-query's cache lands the fresh row immediately.
 */
export function useUpdateSiteAuditConfig(): UseMutationResult<
  Site,
  Error,
  UpdateSiteAuditConfigVariables
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, body }) =>
      apiFetch<Site>(`/v1/management/sites/${uuid}`, {
        method: 'PATCH',
        body: { audit: body },
      }),
    onSuccess: async (data, vars) => {
      // Land the PATCH response straight into the detail cache. When
      // the site is in the origins list, `useSite`'s dedicated query
      // is *disabled* (`enabled: !cached`), so invalidating it is a
      // no-op and the entry would keep the `initialData` snapshot
      // captured at first mount — the settings form then re-seeds the
      // pre-save state on every visit, making saves look reverted.
      qc.setQueryData(siteKeys.detail(vars.uuid), data);
      await qc.invalidateQueries({ queryKey: siteKeys.lists() });
    },
  });
}

/**
 * Toggle a site's `monitoring_enabled` flag via the PATCH endpoint.
 * The hook name is kept for source-compat with call sites that used
 * to drive monitor pause/resume — the underlying behaviour is now
 * "include in scheduled fan-out" since audit config lives on the
 * site.
 */
export function useToggleMonitor(): UseMutationResult<
  Site,
  Error,
  { siteUuid: string; monitoring_enabled: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteUuid, monitoring_enabled }) =>
      apiFetch<Site>(`/v1/management/sites/${siteUuid}`, {
        method: 'PATCH',
        body: { monitoring_enabled },
      }),
    onSuccess: async (_data, vars) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: siteKeys.detail(vars.siteUuid) }),
        qc.invalidateQueries({ queryKey: siteKeys.lists() }),
      ]);
    },
  });
}

/**
 * `audit_site` — fire the full configured matrix for a site (enabled
 * pages × devices × regions). Returns the freshly-created
 * snapshot_group + the audits dispatched.
 */
export function useAuditSite(): UseMutationResult<RunAuditsResponse, Error, { siteUuid: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteUuid }) =>
      apiFetch<RunAuditsResponse>(`/v1/management/sites/${siteUuid}/audits/run`, {
        method: 'POST',
      }),
    onSuccess: async (_data, vars) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: siteKeys.detail(vars.siteUuid) }),
        qc.invalidateQueries({ queryKey: auditKeys.lists() }),
      ]);
    },
  });
}

// `useAuditPage` (fire the matrix for ONE page via
// `/v1/management/pages/:uuid/audits/run`) lived here until the
// page-detail route it served was deprecated (2026-07-26). The API
// endpoint remains for CLI/API callers.

/**
 * `resolve_url` — map a URL to the configured `{site_uuid, page_uuid}`,
 * returning null when nothing matches (404 → null, not thrown). Used
 * by the agent / dashboard discovery flow: resolve first, then call
 * `audit_page` if a page matched, or `audit_site` if only a site
 * matched, or `useSpotAudit` if neither.
 */
export function useResolveUrl(url: string | null) {
  return useQuery({
    queryKey: ['sites', 'resolve', url ?? ''],
    enabled: url != null && url.length > 0,
    queryFn: async (): Promise<{ site_uuid: string; page_uuid: string | null } | null> => {
      if (url == null) return null;
      try {
        return await apiFetch<{ site_uuid: string; page_uuid: string | null }>(
          `/v1/management/sites/resolve?url=${encodeURIComponent(url)}`,
        );
      } catch (err) {
        // 404 is a legitimate "no match" answer — null branch lets
        // the caller render "this URL isn't tracked yet" UX cleanly.
        if (err instanceof Error && err.message.includes('404')) return null;
        throw err;
      }
    },
  });
}

export function invalidateSites(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: siteKeys.all });
}
